// The Local guide block's rules, in a module of its own so both
// templates read one definition and the harness can run it in node.
//
// The block is a page of places around town — coffee before the ride,
// the bike shop, somewhere to eat after — written as a guide for the
// visitor. Most of them are the organization's sponsors, and that is
// the point: a listing somebody reads is worth more to a business than
// a logo in a row. The ♥ mark says which ones support the trails, so
// the guide is honest about that rather than passing an ad off as a
// recommendation.

import { orderSponsors } from './sponsorOrder.js';

/** Places with a name, trimmed. A row with no name is an editor's
 *  half-finished entry and never reaches a visitor. */
export function guidePlaces(items) {
    return (Array.isArray(items) ? items : [])
        .filter((p) => p && String(p.name ?? '').trim() !== '');
}

function categoryOf(place) {
    return String(place?.category ?? '').trim();
}

/** The categories, in the order they first appear, one per spelling
 *  regardless of case — "Coffee" and "coffee " are one chip. */
export function guideCategories(places) {
    const seen = new Map();
    for (const p of places) {
        const c = categoryOf(p);
        if (c && !seen.has(c.toLowerCase())) seen.set(c.toLowerCase(), c);
    }
    return [...seen.values()];
}

/** The places a chip shows. An empty filter is "All". */
export function placesIn(places, category) {
    if (!category) return places;
    const want = category.toLowerCase();
    return places.filter((p) => categoryOf(p).toLowerCase() === want);
}

/**
 * Places in the order a visitor sees them. With shuffle on, supporters
 * take turns at the top of the page — the first slot is the one every
 * business wants — and the editor keeps list order so the preview
 * matches the list beside it. Same seeded shuffle as the Sponsors
 * block, so a re-render never reshuffles under somebody reading.
 */
export function orderPlaces(places, { random = false, inEditor = false, seed = 0 } = {}) {
    return orderSponsors(places, { random, inEditor, seed });
}

/**
 * A place's own website. A block stores whatever the editor typed, so
 * only http(s) is a link; a bare domain ("joescoffee.com") is the most
 * common thing typed and gets https://. Anything else — a javascript:
 * url, a typo with spaces in it — is no link at all.
 */
export function placeWebsite(href) {
    let raw = String(href ?? '').trim();
    if (!raw) return null;
    if (!/^[a-z][a-z0-9+.-]*:/i.test(raw)) {
        if (!/^[^\s/]+\.[^\s/]+/.test(raw)) return null;
        raw = `https://${raw}`;
    }
    let u;
    try {
        u = new URL(raw);
    } catch {
        return null;
    }
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (u.username || u.password) return null;
    return u.href;
}

/** The domain a website link is shown as: "joescoffee.com". */
export function websiteLabel(url) {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return '';
    }
}

/** A tel: link for a typed phone number, or null when it has no digits. */
export function telHref(phone) {
    const raw = String(phone ?? '').trim();
    const digits = raw.replace(/[^\d+]/g, '');
    return digits.replace(/\D/g, '').length >= 3 ? `tel:${digits}` : null;
}

/**
 * Directions to a place. An address alone is how "Main St" opened in
 * another state on the events page, so the block's TOWN is added to
 * any address that does not already name it — "12 Main St" becomes
 * "12 Main St, Stowe, VT", which is something Google resolves.
 */
export function directionsUrl(place, town = '') {
    const address = String(place?.address ?? '').trim();
    if (!address) return null;
    const where = String(town ?? '').trim();
    const query = where && !address.toLowerCase().includes(where.toLowerCase())
        ? `${address}, ${where}`
        : address;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** Category suggestions in the editor. Free text is still accepted. */
export const GUIDE_CATEGORY_SUGGESTIONS = [
    'Coffee', 'Food', 'Brewery', 'Bike shop', 'Outdoor gear', 'Rentals',
    'Lodging', 'Groceries', 'Shuttles & guides', 'Services',
];

export const DEFAULT_SUPPORTER_NOTE = 'Places marked ♥ help pay for our trails. Thank them with a visit.';
